class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper"
    };

    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content"
    };

    this.sectionId = "";
    this.currentPage = 1;
    this.totalPages = 1;
    this.paginationType = "";
    this.activeCol = null;
    this.initialCol = 2;
    this.cachedCol = null;

    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = { mobile: 2, tablet: 4, desktop: 5 };
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();

    initProductCard5();

    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => {
      initProductCard5();
      refreshProductReview();
    });

    this.handleStickyToolbar();
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    if (!section) return;

    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.view = section.dataset.view;
    this.totalPages = productContainer ? Number(productContainer.dataset.totalPages) : 1;

    this.initialCol = Number(section.dataset.initialColumn) || 2;
    this.cachedCol = Number(localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
  };

  initGridView = () => {
    this.toggleView(this.cachedCol);
    this.updateViewByScreen();

    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, btn) => this.toggleView(Number(btn.dataset.column))
    });

    window.addEventListener("resize", debounce(this.updateViewByScreen, 300));
    this.initLoadMore();
  };

  toggleView = (col) => {
    const container = this.domNodes.productContainer;
    if (!container || !col || this.view === "search") return;

    const prev = this.activeCol || this.initialCol;
    container.classList.remove(`m-cols-${prev}`);
    container.classList.add(`m-cols-${col}`);

    this.activeCol = col;
    localStorage.setItem(this.STORAGE_KEY, col);
  };

  updateViewByScreen = () => {
    if (!this.activeCol) return;
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (this.activeCol > maxCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () =>
    window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";

  initFilters = () => {
    if (!this.domNodes.sidebar) return;
    const { sidebar, openSidebar, closeSidebar, section } = this.domNodes;

    openSidebar?.addEventListener("click", () => {
      sidebar.style.display = "block";
      requestAnimationFrame(() => sidebar.style.setProperty("--m-bg-opacity", "0.5"));
      section.classList.add("sidebar-open");
    });

    closeSidebar?.addEventListener("click", () => {
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
      section.classList.remove("sidebar-open");
    });
  };

  initMobileSorting = () => {
    const { openSorting, closeSorting, sortingWrapper, sortingList } = this.mbSortingNodes;
    if (!openSorting) return;

    openSorting.addEventListener("click", () => {
      sortingWrapper.style.display = "block";
      requestAnimationFrame(() => {
        sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
        sortingList.style.setProperty("--m-translate-y", "0");
      });
    });

    closeSorting.addEventListener("click", () => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0");
      sortingList.style.setProperty("--m-translate-y", "100%");
      setTimeout(() => sortingWrapper.style.removeProperty("display"), 300);
    });
  };

  initLoadMore = () => {
    if (this.paginationType !== "infinite" || this.totalPages <= 1) return;

    const { loadMoreBtn } = this.domNodes;
    if (!loadMoreBtn) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting && this.loadMoreProducts());
    }, { threshold: 1 });

    observer.observe(loadMoreBtn);
  };

  loadMoreProducts = () => {
    const next = this.currentPage + 1;
    if (next > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;

    fetchSection(this.sectionId, { params: { page: next } })
      .then(html => {
        html.querySelectorAll("[data-product-container] .m-product-item")
          .forEach(el => productContainer.appendChild(el));
      })
      .finally(() => {
        this.currentPage = next;
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        if (next >= this.totalPages) loadMoreBtn?.remove();
      });
  };

  initProducts = () => {
    MinimogTheme?.Wishlist?.setWishlistButtonsState();
    MinimogTheme?.CompareProduct?.setCompareButtonsState();
  };

  handleStickyToolbar = () => {
    const toolbar = this.domNodes.toolbar;
    if (!toolbar) return;

    let lastScroll = 0;
    const offset = toolbar.offsetTop;

    window.addEventListener("scroll", () => {
      const y = window.scrollY;
      if (y <= offset) {
        toolbar.classList.remove("m-collection-toolbar--sticky");
        return;
      }
      toolbar.classList.add("m-collection-toolbar--sticky");
      toolbar.classList.toggle("scroll-down", y > lastScroll);
      toolbar.classList.toggle("scroll-up", y < lastScroll);
      lastScroll = y;
    });
  };
}

/* =====================================================
   PRODUCT CARD 5 – MOBILE SWIPE INDICATOR
===================================================== */
function initProductCard5Scroll() {
  document.querySelectorAll('.m-product-card--style-5 [data-pcard5]').forEach(card => {
    if (card.dataset.ready) return;
    card.dataset.ready = 'true';

    const track = card.querySelector('.m-pcard5-track');
    const bar = card.querySelector('.m-pcard5-indicator-bar');
    const slides = track?.children.length || 1;

    if (!track || !bar || slides <= 1) return;

    bar.style.width = `${100 / slides}%`;

    const onScroll = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 0) return;
      const progress = track.scrollLeft / max;
      bar.style.transform = `translateX(${progress * (100 - 100 / slides)}%)`;
    };

    track.addEventListener('scroll', onScroll, { passive: true });
  });
}

document.addEventListener('DOMContentLoaded', initProductCard5Scroll);
document.addEventListener('shopify:section:load', initProductCard5Scroll)
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.m-product-card--style-5 .m-pcard5-media').forEach(media => {
    const track = media.querySelector('.m-pcard5-track');
    const indicator = media.querySelector('.m-pcard5-indicator');
    const bar = media.querySelector('.m-pcard5-indicator-bar');

    if (!track || !indicator || !bar) return;

    const slides = track.querySelectorAll('.m-pcard5-slide');
    const count = slides.length;

    if (count <= 1) {
      indicator.style.display = 'none';
      return;
    }

    // 🔹 largeur du trait = 1 / nombre d’images
    const barWidth = 100 / count;
    bar.style.width = `${barWidth}%`;

    const updateIndicator = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) return;

      const progress = track.scrollLeft / maxScroll;
      const maxTranslate = 100 - barWidth;

      bar.style.transform = `translateX(${progress * maxTranslate}%)`;
    };

    track.addEventListener('scroll', updateIndicator, { passive: true });
  });
});;